<template>
  <!-- XOL-645: the stored copy of ONE request e-mail, opened from a recipient row's "View e-mail"
       in MailBatchDialog (stacked over it). It exists only where the API captures mail instead
       of sending it (dev, staging, demo); on production `has_preview` is never true, so nothing
       opens this. READ-ONLY: Close is the only button, there is no resend and no download.
       The data is GET /actions/{id}/mail/{batch}/recipients/{log}/preview, fetched on every open.

       XOL-602/603/604: the same dialog also shows an ACCOUNT e-mail (a password link or a
       "password changed" notice), opened from AccountMailList. That caller passes `previewPath`
       (contacts/{id}/account-mail/{log}/preview) and `copyPrefix` ('accountMail.preview'); the
       dialog then fetches that path and reads its words from that prefix. An INVITATION e-mail
       (ContactInvitesPanel) does the same with contacts/invites/{id}/mail/{log}/preview and
       'contactInvite.preview'. With `previewPath` null (every request caller) the URL and the
       copy are exactly what they were. The sandbox below is the same for all of them.

       SECURITY: the e-mail's HTML is NEVER put in this component's own DOM (no v-html). It goes
       in a sandboxed <iframe srcdoc> built by lib/mailPreviewDoc.js. `sandbox` is a STATIC
       attribute (never bind it): no allow-scripts, no allow-same-origin, no allow-top-navigation.
       Only the two popup tokens, so a link in the e-mail opens a normal new tab (D1).
       See docs/UX-architecture/conventions.md, "Rendering stored HTML". -->
  <el-dialog
    :visible="visible"
    :title="dialogTitle"
    append-to-body
    :width="isMediumOrLess ? '92%' : '760px'"
    top="6vh"
    :custom-class="customClass"
    @open="onOpen"
    @opened="onOpened"
    @close="onDialogClose"
    @closed="onClosed">

    <info-dialog-header slot="title" ref="header"
      icon="fa-envelope-open"
      :title="headerTitle"
      :subtitle="recipientLine"
      :compact="isMediumOrLess" />

    <div class="mail-preview" :class="{ 'is-empty': !!error }" ref="body" tabindex="-1"
      v-loading="loading" :element-loading-text="copy('loading')">

      <template v-if="error">
        <el-alert type="error" :title="errorText" show-icon :closable="false" />
        <el-button v-if="error === 'generic'" size="small" class="mail-preview__retry" @click="load">{{copy('retry')}}</el-button>
      </template>

      <template v-if="data">
        <el-alert class="mail-preview__notice" type="info" show-icon :closable="false" :title="copy('notice')" :description="noticeDescription" />

        <info-meta-list :rows="metaRows" :narrow="isMediumOrLess" />

        <div class="mail-preview__viewport">
          <iframe v-if="srcdoc" class="mail-preview__frame" ref="frame"
            sandbox="allow-popups allow-popups-to-escape-sandbox"
            referrerpolicy="no-referrer"
            :srcdoc="srcdoc"
            :title="frameTitle"></iframe>
        </div>
      </template>
    </div>

    <!-- Read-only: Close is the only button, and it is never disabled. -->
    <div slot="footer" class="mail-preview__footer">
      <el-button size="small" :class="{ 'is-block': isMediumOrLess }" @click="requestClose">{{copy('close')}}</el-button>
    </div>

  </el-dialog>
</template>

<script>
import axios from '@/lib/axiosClient'
import DateMixins from '@/mixins/DateMixins'
import InfoDialogHeader from '@/components/requests/InfoDialogHeader'
import InfoMetaList from '@/components/requests/InfoMetaList'
import { activityText } from '@/lib/requestActivity'
import { previewSrcdoc } from '@/lib/mailPreviewDoc'

export default {
  name: 'mail-preview-dialog',

  mixins: [DateMixins],

  components: {
    InfoDialogHeader,
    InfoMetaList
  },

  props: {
    // The parent owns it and clears it on `close`. Every `false -> true` fetches again.
    visible: {
      type: Boolean,
      default: false
    },

    // The request the batch belongs to.
    actionId: {
      type: [Number, String],
      default: null
    },

    // An OPAQUE string (the API's 26-character ULID): never transformed.
    batchId: {
      type: String,
      default: null
    },

    // The ledger row (request_mail_log.id) whose stored copy is shown.
    logId: {
      type: [Number, String],
      default: null
    },

    // The address the ledger row is for (the intended recipient).
    email: {
      type: String,
      default: ''
    },

    // The address the mail was redirected to (non-production), else null.
    deliveredTo: {
      type: String,
      default: null
    },

    // The batch's subject: shown before the fetch lands, and as a fallback.
    subject: {
      type: String,
      default: null
    },

    // The batch dialog's header title ("Upload notice"): names the e-mail the user just saw.
    label: {
      type: String,
      default: null
    },

    // The row's second line ("{User name} · {Telco} (Telco)"): who the copy is for.
    recipientLine: {
      type: String,
      default: null
    },

    // XOL-602/603/604: where an ACCOUNT e-mail's stored copy is, relative to the API root
    // (contacts/{id}/account-mail/{log}/preview). Null for a request e-mail, which is built from
    // actionId / batchId / logId exactly as before.
    previewPath: {
      type: String,
      default: null
    },

    // The prefix every word of the dialog is looked up under in requestActivity.js.
    copyPrefix: {
      type: String,
      default: 'requestMail.preview'
    },

    // An account e-mail that carries a link says what the link does under the notice
    // (`<copyPrefix>.noticeLink`). False for "password changed", which has none.
    linkNotice: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      data: null,
      loading: false,
      // null | 'generic' | 'notFound' | 'forbidden'
      error: null,
      seq: 0,
      opened: false,
      opener: null
    }
  },

  computed: {
    // What the dialog is showing: null while it is closed.
    fetchKey () {
      if (this.previewPath) {
        return this.visible ? this.previewPath : null
      }

      return this.visible && this.batchId && this.logId ? this.actionId + '/' + this.batchId + '/' + this.logId : null
    },

    // Only an account e-mail with a link has one; a request e-mail's notice is the title alone.
    // The invitation's own (contactInvite.preview.noticeLink) names the invited address, so the
    // address goes along; a string entry ignores the params.
    noticeDescription () {
      return this.linkNotice ? this.copy('noticeLink', { email: this.email }) : undefined
    },

    customClass () {
      return 'xol-mail-preview-dialog xol-info-dialog' + (this.error ? '' : ' is-sized') + (this.isMediumOrLess ? ' is-narrow' : '')
    },

    // The dialog's accessible name: "Upload notice to user.one@example.com".
    dialogTitle () {
      return this.copy('title', { label: this.label, email: this.email })
    },

    headerTitle () {
      return this.label || this.text('requestMail.label.other')
    },

    // The real subject: the stored copy's, else the batch's.
    subjectValue () {
      return (this.data && this.data.subject) || this.subject || null
    },

    frameTitle () {
      return this.copy('frameTitle', { subject: this.subjectValue })
    },

    // The frame's document. Built here from the stored HTML; the HTML itself never reaches this
    // component's template.
    srcdoc () {
      return this.data ? previewSrcdoc(this.data.html) : ''
    },

    fromText () {
      var from = this.data ? this.data.from : null

      return from && from.address ? this.copy('address', { name: from.name, address: from.address }) : null
    },

    // The ledger's address; when the stored copy went to another one (a test redirect) it says so.
    // Never "delivered": nothing was.
    toText () {
      var email = this.email || ''
      var addresses = this.data && Array.isArray(this.data.to) ? this.data.to : []
      var copyTo = addresses.map(function (a) { return a && a.address ? a.address : null }).filter(Boolean).join(', ') || this.deliveredTo || ''

      if (copyTo && copyTo.trim().toLowerCase() !== email.trim().toLowerCase()) {
        return this.copy('redirected', { email: email, address: copyTo })
      }

      return email
    },

    metaRows () {
      var rows = [
        { key: 'subject', label: this.copy('meta.subject'), value: this.subjectValue || this.copy('noSubject') }
      ]

      if (this.fromText) {
        rows.push({ key: 'from', label: this.copy('meta.from'), value: this.fromText })
      }

      rows.push({ key: 'to', label: this.copy('meta.to'), value: this.toText })

      var captured = this.data && this.data.stored_at ? this.formatDateTime(this.data.stored_at) : null

      if (captured) {
        rows.push({ key: 'captured', label: this.copy('meta.captured'), value: captured })
      }

      return rows
    },

    errorText () {
      if (this.error === 'forbidden') {
        return this.copy('forbidden', { towerCo: this.towerCoName() })
      }

      return this.copy(this.error === 'notFound' ? 'notFound' : 'error')
    }
  },

  watch: {
    // Every open fetches again: nothing is cached between opens. A different row while open
    // fetches that one. The previous copy is dropped FIRST, so another recipient's e-mail never
    // shows under the loading mask.
    fetchKey (now) {
      if (now) {
        this.data = null
        this.load()
      } else {
        this.seq++
        this.loading = false
        this.opened = false
      }
    }
  },

  mounted () {
    if (this.visible) {
      this.opener = typeof document !== 'undefined' ? document.activeElement : null
      this.load()
    }
  },

  methods: {
    text (key, params) {
      return activityText(key, params)
    },

    // One word of the dialog, under the caller's prefix ('requestMail.preview' unless an account
    // e-mail asked for its own). activityText throws on an unknown key, so every key looked up
    // here is defined under both prefixes.
    copy (key, params) {
      return activityText(this.copyPrefix + '.' + key, params)
    },

    load () {
      var self = this
      var seq = ++this.seq

      if (!this.previewPath && (!this.actionId || !this.batchId || !this.logId)) {
        return Promise.resolve()
      }

      this.loading = true
      this.error = null

      // An account e-mail's path already names the contact, so the current org is not attached.
      var url = this.previewPath
        ? this.getEndpoint(this.previewPath, null, false)
        : this.getEndpoint('/actions/' + this.actionId + '/mail/' + this.batchId + '/recipients/' + this.logId + '/preview')

      return axios.get(url).then(function (response) {
        if (seq !== self.seq) {
          return
        }

        var body = response ? response.data : null

        if (body && typeof body.html !== 'string' && body.data) {
          body = body.data
        }

        // An empty or missing html is the generic error: there is nothing to show.
        if (!body || typeof body.html !== 'string' || body.html === '') {
          self.fail('generic')
          return
        }

        self.data = body
        self.loading = false
        self.focusSummary()
      }, function (err) {
        if (seq !== self.seq) {
          return
        }

        var status = err && err.response ? err.response.status : null

        console.error('XOL-645: could not load the e-mail preview', status, err)
        self.fail(status === 403 ? 'forbidden' : (status === 404 ? 'notFound' : 'generic'))
      })
    },

    fail (kind) {
      this.error = kind
      this.loading = false
      this.data = null
    },

    // Focus lands on the header once the dialog has opened (and again when the answer is in).
    focusSummary () {
      var self = this

      if (!this.opened) {
        return
      }

      this.$nextTick(function () {
        var el = self.$refs.header || self.$refs.body

        if (el && typeof el.focus === 'function') {
          el.focus()
        }
      })
    },

    onOpen () {
      this.opener = typeof document !== 'undefined' ? document.activeElement : null
    },

    onOpened () {
      this.opened = true
      this.focusSummary()
    },

    // Element closed itself (the ×, Esc, the backdrop): hand it to the parent. A close the
    // parent started (`visible` already false) is not announced twice.
    onDialogClose () {
      if (this.visible) {
        this.$emit('close')
      }
    },

    requestClose () {
      this.$emit('close')
    },

    // The dialog has closed: the copy leaves the DOM, and focus goes back to what opened it.
    onClosed () {
      var opener = this.opener

      this.opened = false
      this.opener = null
      this.data = null

      if (opener && typeof opener.focus === 'function' && typeof document !== 'undefined' && document.body.contains(opener)) {
        opener.focus()
      }

      // The parent may now drop it (MailBatchDialog does).
      this.$emit('closed')
    }
  }
}
</script>

<style>
/* The shell, for this dialog only. Not scoped: these are Element's own elements, and the shared
   `.xol-info-dialog` rules (70vh body that scrolls) are overridden here, not edited. There is no
   scroll inside a scroll: the dialog has a fixed height, the body does not scroll, and the frame
   takes what is left and scrolls its own content. `overflow-y: auto` on the body is only a
   safety valve for a pathologically tall subject. The error states drop `is-sized`. */
.xol-mail-preview-dialog.xol-info-dialog .el-dialog__body { max-height: none; }
.xol-mail-preview-dialog.is-sized { display: flex; flex-direction: column; height: 88vh; max-height: 880px; min-height: 520px; margin-bottom: 0; }
.xol-mail-preview-dialog.is-sized .el-dialog__header,
.xol-mail-preview-dialog.is-sized .el-dialog__footer { flex: none; }
.xol-mail-preview-dialog.is-sized .el-dialog__body { flex: 1 1 auto; min-height: 0; display: flex; flex-direction: column; overflow-y: auto; }
.xol-mail-preview-dialog.is-sized.is-narrow { min-height: 640px; max-height: none; }
</style>

<style scoped>
.mail-preview { flex: 1 1 auto; min-height: 0; display: flex; flex-direction: column; outline: none; }
.mail-preview.is-empty { min-height: 200px; }
.mail-preview__notice, .info-meta { flex: none; }
.mail-preview__notice { margin-bottom: 12px; }
.mail-preview__viewport { flex: 1 1 auto; min-height: 240px; border: 1px solid #EBEEF5; border-radius: 4px; overflow: hidden; background: #FFFFFF; }
.mail-preview__frame { display: block; width: 100%; height: 100%; border: 0; background: #FFFFFF; }
/* .mail-preview is a flex column: without align-self the button would stretch to full width. */
.mail-preview__retry { margin-top: 8px; align-self: flex-start; }
.mail-preview__footer { text-align: right; }
.mail-preview__footer .is-block { width: 100%; }
</style>
